// frontend/src/pages/ManagerSupportTickets.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container, Paper, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, TextField,
  Select, MenuItem, FormControl, InputLabel, Alert, Snackbar, Tabs, Tab
} from '@mui/material';
import { Visibility, Assignment, CheckCircle } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const StatusChip = ({ status }) => {
  const colors = {
    'Open': { bg: '#ffebee', color: '#c62828' },
    'In Progress': { bg: '#fff3e0', color: '#ef6c00' },
    'Resolved': { bg: '#e8f5e9', color: '#2e7d32' },
    'Closed': { bg: '#e0e0e0', color: '#616161' }
  };
  return (
    <Chip label={status} size="small" sx={{ bgcolor: colors[status]?.bg, color: colors[status]?.color, fontWeight: 500 }} />
  );
};

const PriorityChip = ({ priority }) => {
  const colors = {
    'Low': { bg: '#e8f5e9', color: '#2e7d32' },
    'Medium': { bg: '#fff3e0', color: '#ef6c00' },
    'High': { bg: '#ffebee', color: '#c62828' },
    'Urgent': { bg: '#fce4ec', color: '#d81b60' }
  };
  return (
    <Chip label={priority} size="small" sx={{ bgcolor: colors[priority]?.bg, color: colors[priority]?.color, fontWeight: 500 }} />
  );
};

function ManagerSupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignTo, setAssignTo] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [resolution, setResolution] = useState('');
  const [updateStatus, setUpdateStatus] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [stats, setStats] = useState({ total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0 });

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/support/manager/tickets`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setTickets(response.data.data || []);
      setStats(response.data.stats || {});
    } catch (error) {
      showNotification('Failed to fetch tickets', 'error');
    } finally {
      setLoading(false);
    }
  };

  const viewTicketDetails = async (ticket) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/support/tickets/${ticket.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setSelectedTicket(response.data.data);
      setUpdateStatus(response.data.data.status);
      setResolution(response.data.data.resolution || '');
      setOpenDialog(true);
    } catch (error) {
      showNotification('Failed to load ticket details', 'error');
    }
  };

  const handleUpdateTicket = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/support/manager/tickets/${selectedTicket.id}`, 
        { status: updateStatus, resolution ,},{
           headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      showNotification('Ticket updated successfully', 'success');
      setOpenDialog(false);
      fetchTickets();
    } catch (error) {
      showNotification('Failed to update ticket', 'error');
    }
  };

  const handleAssignTicket = async () => {
    if (!assignTo) {
      showNotification('Please select a department', 'error');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/support/manager/tickets/${selectedTicket.id}/assign`,
        { assignTo, notes: assignNotes ,},{
           headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      showNotification(`Ticket assigned to ${assignTo}`, 'success');
      setAssignDialog(false);
      setAssignTo('');
      setAssignNotes('');
      fetchTickets();
      setOpenDialog(false);
    } catch (error) {
      showNotification('Failed to assign ticket', 'error');
    }
  };

  const showNotification = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight="600" sx={{ mb: 3 }}>Team Tickets</Typography>
      
      {/* Stats Cards */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" color="primary">{stats.total}</Typography>
          <Typography variant="caption">Total Tickets</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" color="error">{stats.open}</Typography>
          <Typography variant="caption">Open</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" color="warning.main">{stats.inProgress}</Typography>
          <Typography variant="caption">In Progress</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" color="success.main">{stats.resolved}</Typography>
          <Typography variant="caption">Resolved</Typography>
        </Paper>
      </Box>

      {/* Tickets Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#fafafa' }}>
            <TableRow>
              <TableCell>Ticket #</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tickets.map((ticket) => (
              <TableRow key={ticket.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="500">{ticket.ticket_number}</Typography>
                </TableCell>
                <TableCell>{ticket.employee?.firstName || 'N/A'}</TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{ticket.subject}</Typography>
                </TableCell>
                <TableCell><Chip label={ticket.category} size="small" /></TableCell>
                <TableCell><PriorityChip priority={ticket.priority} /></TableCell>
                <TableCell><StatusChip status={ticket.status} /></TableCell>
                <TableCell><Chip label={ticket.assigned_to} size="small" variant="outlined" /></TableCell>
                <TableCell>{new Date(ticket.createdAt).toLocaleDateString()}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => viewTicketDetails(ticket)}>
                    <Visibility />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Details Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        {selectedTicket && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{selectedTicket.ticket_number}</Typography>
                <StatusChip status={selectedTicket.status} />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">Employee</Typography>
                <Typography>{selectedTicket.employee?.firstName} {selectedTicket.employee?.lastName}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">Subject</Typography>
                <Typography fontWeight="500">{selectedTicket.subject}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                  <Typography>{selectedTicket.description}</Typography>
                </Paper>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">Current Assignment</Typography>
                <Chip label={selectedTicket.assigned_to} sx={{ mt: 1 }} />
              </Box>

              {/* Resolution field */}
              <TextField
                fullWidth
                label="Resolution Notes"
                multiline
                rows={3}
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                sx={{ mb: 2 }}
              />

              {/* Status update */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Update Status</InputLabel>
                <Select value={updateStatus} onChange={(e) => setUpdateStatus(e.target.value)}>
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Resolved">Resolved</MenuItem>
                  <MenuItem value="Closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button color="primary" onClick={() => setAssignDialog(true)}>
                Assign to Department
              </Button>
              <Button onClick={handleUpdateTicket} variant="contained" color="primary">
                Update Ticket
              </Button>
              <Button onClick={() => setOpenDialog(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Ticket to Department</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Assign To</InputLabel>
            <Select value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
              <MenuItem value="HR">HR Department</MenuItem>
              <MenuItem value="Finance">Finance Department</MenuItem>
              {/* <MenuItem value="IT">IT Department</MenuItem>
              <MenuItem value="Admin">Admin</MenuItem> */}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Notes (Optional)"
            multiline
            rows={2}
            value={assignNotes}
            onChange={(e) => setAssignNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(false)}>Cancel</Button>
          <Button onClick={handleAssignTicket} variant="contained" color="primary">
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
}

export default ManagerSupportTickets;